import React, { useState, Suspense, lazy, useEffect } from "react";
import { colors } from "../../assets/components/ai/constants";
import { Loader2 } from "lucide-react";
const Checkboxtype = lazy(() => import("../QuestionTypes/Checkboxtype"));
const ImageMCQ = lazy(() => import("../QuestionTypes/ImageMCQ"));
const HorizontalMCQ = lazy(() => import("../QuestionTypes/HorizontalMCQ"));
const Shapetype = lazy(() => import("../QuestionTypes/Shapetype"));
const Entertext = lazy(() => import("../QuestionTypes/Entertext"));
const Dots = lazy(() => import("../QuestionTypes/Dots"));
const VerticalImageMcq = lazy(() => import("../QuestionTypes/VerticalImageMCQ"));
const EvenOddtype = lazy(() => import("../QuestionTypes/EvenOddtype"));
const EvenOddFishType = lazy(() => import("../QuestionTypes/EvenOddFishType"));
const EvenOddSpecial = lazy(() => import("../QuestionTypes/EvenOddSpecial"));
const BalanceQuestion = lazy(() => import("../QuestionTypes/BalanceQuestion"));
const DrageAndWrite = lazy(() => import("../QuestionTypes/DrageAndWrite"));
const DrawText = lazy(() => import("../QuestionTypes/DrawText"));
const ClockQuestion = lazy(() => import("../QuestionTypes/ClockQuestion"));
const AppleDrop = lazy(() => import("../QuestionTypes/AppleDrop"));
const FlowerDrop = lazy(() => import("../QuestionTypes/FlowerDrop"));
const CircleDrop = lazy(() => import("../QuestionTypes/CircleDrop"));
const ImageOrderDrop = lazy(() => import("../QuestionTypes/ImageOrderDrop"));
const FaceTypeQuestion = lazy(() => import("../QuestionTypes/FaceTypeQuestion"));
const ColorStar = lazy(() => import("../QuestionTypes/ColorStar"));
const Orderdrop = lazy(() => import("../QuestionTypes/Orderdrop/Orderdrop"));
const ChooseBiggerQuestion = lazy(() => import("../QuestionTypes/ChooseBiggerQuestion"));
const CoinTypeQuestion = lazy(() => import("../QuestionTypes/CoinTypeQuestion"));
const CalendarMonthType = lazy(() => import("../QuestionTypes/CalendarMonthType"));
const Slidertype = lazy(() => import("../QuestionTypes/Slidertype"));
const TextAndSequence = lazy(() => import("../QuestionTypes/TextAndSequence"));
const CalendarDateType = lazy(() => import("../QuestionTypes/CalendarDateType"));
const Reflection = lazy(() => import("../QuestionTypes/Reflection"));
const NoQuestionSelect = lazy(() => import("../QuestionTypes/NoQuestionSelect"));
const SeasonType = lazy(() => import("../QuestionTypes/SeasonType"));
const ReplaceValues = lazy(() => import("../QuestionTypes/ReplaceValues"));
const MatchType = lazy(() => import("../QuestionTypes/MatchType"));
const Sortbox = lazy(() => import("../QuestionTypes/Sortbox"));
const DetailedMCQ = lazy(() => import("../QuestionTypes/DetailedMCQ"));
const Multipletext = lazy(() => import("../QuestionTypes/Multipletext"));
const Coordinateplane = lazy(() => import("../QuestionTypes/Coordinateplane/Coordinateplane"));
const Pointsplot = lazy(() => import("../QuestionTypes/Pointsplot"));
const BarGraph = lazy(() => import("../QuestionTypes/BarGraph"));
const MultipleSelectImg = lazy(() => import("../QuestionTypes/MultipleSelectImg"));
const ColorCircle = lazy(() => import("../QuestionTypes/ColorCircle"));
const ColorSquare = lazy(() => import("../QuestionTypes/ColorSquare"));
const CountingQuestion = lazy(() => import("../QuestionTypes/CountingQuestion"));

// Foundation question type wrappers
const DrawTextWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.DrawTextWrapper })));
const MCQFoundation = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.MCQWrapper })));
const SelectNumFoundation = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.SelectNumWrapper })));
const TxtWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.TxtWrapper })));
const MultipleSelectImgWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.MultipleSelectImgWrapper })));
const CardFlipWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.CardFlipWrapper })));
const DrageAndDropWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.DrageAndDropWrapper })));
const ListenAndIDentifyWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.ListenAndIDentifyWrapper })));
const PatternMatchingWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.PatternMatchingWrapper })));
const DiceWrapper = lazy(() => import("../QuestionTypes/FoundationWrappers").then((m) => ({ default: m.DiceWrapper })));

const DrawTextDispatcher = (props) => {
  const qImgList = props.questionData?.questionimagelist || [];
  const firstItem = String(qImgList[0] || "");
  const shapes = ["circle", "triangle", "rectangle", "square", "pentagon", "hexagon"];
  const isShape = shapes.includes(firstItem.toLowerCase());
  const ResolvedComponent = isShape ? DrawText : DrawTextWrapper;
  return <ResolvedComponent {...props} />;
};

const TextMCQ = (props) => {
  const validQuestionImages = props.questionData?.questionimagelist?.filter((item) => item && item !== ".") || [];
  const ResolvedComponent = validQuestionImages.length > 1 ? Shapetype : HorizontalMCQ;
  return <ResolvedComponent {...props} />;
};

const componentMap = {
  Shapetype: Shapetype,
  Checkboxtype: Checkboxtype,
  ImageMCQ: ImageMCQ,
  EvenOddtype: EvenOddtype,
  EvenOddFishType: EvenOddFishType,
  TextAndSequence: TextAndSequence,
  Slidertype: Slidertype,
  Reflection: Reflection,
  MatchType: MatchType,
  HorizontalMCQ: HorizontalMCQ,
  Entertext: Entertext,
  Dots: Dots,
  Orderdrop: Orderdrop,
  VerticalImageMcq: VerticalImageMcq,
  ReplaceValues: ReplaceValues,
  Sortbox: Sortbox,
  DetailedMCQ: DetailedMCQ,
  Multipletext: Multipletext,
  Coordinateplane: Coordinateplane,
  BarGraph: BarGraph,
  Pointsplot: Pointsplot,
  TextMCQ: TextMCQ,
  ColorCircle: ColorCircle,
  ColorSquare: ColorSquare,
  MultipleSelectImg: MultipleSelectImgWrapper, // Foundation or main

  DrawText: DrawTextDispatcher,
  MCQ: MCQFoundation,
  SelectNum: SelectNumFoundation,
  Txt: TxtWrapper,
  CardFlip: CardFlipWrapper,
  DrageAndDrop: DrageAndDropWrapper,
  ListenAndIDentify: ListenAndIDentifyWrapper,
  PatternMatching: PatternMatchingWrapper,
  Dice: DiceWrapper,

  Counting: CountingQuestion,
  AppleDrop: AppleDrop,
  Circldrop: CircleDrop,
  clock: ClockQuestion,
  Calenderdatetype: CalendarDateType,
  Calendermonthtype: CalendarMonthType,
  flowerdrop: FlowerDrop,
  Imageorderdrop: ImageOrderDrop,
  Noquestionselect: NoQuestionSelect,
  Seasontype: SeasonType,
  facetype: FaceTypeQuestion,
  Choosebigger: ChooseBiggerQuestion,
  Cointype: CoinTypeQuestion,
  Balance: BalanceQuestion,
  EvenOddSpecial: EvenOddSpecial,
  DrageAndWrite: DrageAndWrite,
  AnswerText: FaceTypeQuestion,
  ColorStar: ColorStar,
};

const resolveQuestionType = (fields = {}, lessonId, yearId, questionIndex) => {
  const qtOriginal = String(fields?.questionTypeOriginal || "").trim().toLowerCase();
  const qt1 = String(fields?.questionType || "").trim();

  if ((qt1 === "Noquestionselect" || qtOriginal === "noquestionselect") && questionIndex === 3 && yearId === "1") {
    return "VerticalImageMcq";
  }
  if ((qt1 === "VerticalTextMCQ" || qtOriginal === "verticaltextmcq" || qt1 === "VerticalImageMcq" || qtOriginal === "verticalimagemcq") && questionIndex === 6) {
    return "VerticalImageMcq";
  }

  if (fields?.QuestionId === "279" || fields?.QuestionId === "295" || (String(yearId) === "4" && (String(lessonId) === "T1" || String(lessonId) === "95") && questionIndex === 0)) {
    return "EvenOddSpecial";
  }

  const qt = String(fields?.questionType || "").trim();
  const normalizedQuestionType = qt.toLowerCase();

  if (normalizedQuestionType === "facetype") return "facetype";
  if (normalizedQuestionType === "shapetype") return "Shapetype";

  const anyFieldHasStar = Object.values(fields).some((v) => typeof v === "string" && v.toLowerCase().includes("star"));
  if (String(yearId) === "4" && (anyFieldHasStar || String(lessonId) === "898" || String(lessonId) === "6")) {
    return "ColorStar";
  }

  if (qtOriginal === "colorcircle") return "ColorCircle";
  if (qtOriginal === "colorsquare" || qtOriginal === "colorrectangle") return "ColorSquare";

  if (qt === "ImageMCQ" && String(yearId) === "2") return "VerticalImageMcq";

  if ((qt === "EvenOddtype" || qtOriginal === "evenoddtype") && fields?.imageList?.length > 0) {
    return "EvenOddFishType";
  }

  return fields?.questionType;
};

const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return <div>This type is not handled yet.</div>;
  }

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[320px] w-full">
          <Loader2 className="h-8 w-8 animate-spin text-white" />
        </div>
      }
    >
      {React.Children.map(children, (child) =>
        React.cloneElement(child, { onError: () => setHasError(true) })
      )}
    </Suspense>
  );
};

const HandleAIQuestions = ({
  questions,
  yearId,
  onQuestionsComplete,
  colorTheme = colors.primaryDarkBlue,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [attempts, setAttempts] = useState({});
  const [timeTaken, setTimeTaken] = useState({});
  const [startTime, setStartTime] = useState(Date.now());

  useEffect(() => {
    setStartTime(Date.now());
  }, [currentQuestionIndex]);

  useEffect(() => {
    setCurrentQuestionIndex(0);
    setAttempts({});
    setTimeTaken({});
    setStartTime(Date.now());
  }, [questions, yearId]);

  const handleAnswer = (_isCorrect = true, attemptCount = 1, questionTime = 0) => {
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion) return;

    const questionId = currentQuestion.pk.toString();
    const normalizedAttemptCount = Number(attemptCount);
    const safeAttemptCount =
      Number.isFinite(normalizedAttemptCount) && normalizedAttemptCount > 0
        ? normalizedAttemptCount
        : 1;
    const normalizedQuestionTime = Number(questionTime);
    const elapsedQuestionTime = (Date.now() - startTime) / 1000;
    const safeQuestionTime =
      Number.isFinite(normalizedQuestionTime) && normalizedQuestionTime >= 0
        ? normalizedQuestionTime
        : elapsedQuestionTime;

    const newAttempts = {
      ...attempts,
      [questionId]: safeAttemptCount,
    };

    const newTimeTaken = {
      ...timeTaken,
      [questionId]: parseFloat(safeQuestionTime.toFixed(3)),
    };

    setAttempts(newAttempts);
    setTimeTaken(newTimeTaken);

    if (currentQuestionIndex === questions.length - 1) {
      const lessonIds = {};
      const yearIds = {};

      questions.forEach((q) => {
        const qId = q.pk.toString();
        lessonIds[qId] = q.fields.lession.toString();
        yearIds[qId] = yearId.toString();

        if (!newAttempts[qId]) newAttempts[qId] = 0;
        if (!newTimeTaken[qId]) newTimeTaken[qId] = 0;
      });

      const payload = {
        NumberOfAttempts: newAttempts,
        TimeTaken: newTimeTaken,
        lessonIds,
        yearIds,
      };
      onQuestionsComplete(payload);
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  let resolvedType = currentQuestion ? resolveQuestionType(
    currentQuestion.fields,
    currentQuestion.fields?.lession,
    yearId,
    currentQuestionIndex
  ) : null;

  // Trim whitespace
  if (resolvedType) {
    resolvedType = resolvedType.trim();
  }

  // Find exact or case-insensitive match in componentMap
  const componentKey = resolvedType ? (
    componentMap[resolvedType] ? resolvedType : Object.keys(componentMap).find(
      key => key.toLowerCase() === resolvedType.toLowerCase()
    )
  ) : null;

  const QuestionComponent = componentKey ? componentMap[componentKey] : null;

  return (
    <div className="ai-question-host w-full items-center">
      <div className="ai-question-host-inner flex-1 flex items-center justify-center">
        <ErrorBoundary>
          {QuestionComponent ? (
            <div className="ai-question-component-wrap">
              <QuestionComponent
                key={`question-${currentQuestion.pk}`}
                questionData={currentQuestion.fields}
                onCorrectAnswer={handleAnswer}
                colorTheme={colors.primaryDarkBlue}
                attempts={attempts[currentQuestion.pk.toString()]}
                yearId={yearId}
              />
            </div>
          ) : (
            <div className="text-white text-xl flex flex-col items-center">
              <div>This question type ({resolvedType || currentQuestion.fields?.questionType}) is not supported yet.</div>
              <div className="text-sm mt-2 text-red-300">
                Debug: qt='{currentQuestion.fields?.questionType}' | 
                resolved='{resolvedType}' | 
                key='{componentKey}' |
                inMap={componentMap["DrawText"] ? "yes" : "no"} |
                typeOf={typeof componentMap["DrawText"]}
              </div>
            </div>
          )}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default React.memo(HandleAIQuestions);
